import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

/**
 * Clear the app-icon badge and remove delivered pushes from the notification
 * center. Call when the user has seen ALL their notifications (opening the
 * Notifications screen, which marks every row read). No-op on web.
 */
export async function clearAppBadge(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Badge.clear();
  } catch (error) {
    logger.error('Failed to clear app icon badge:', error);
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    logger.error('Failed to remove delivered notifications:', error);
  }
}

/**
 * Optimistically knock one off the icon badge for instant feedback when a
 * push is tapped. Purely cosmetic — always follow with syncAppBadgeToUnread,
 * which corrects the badge to the true unread count. No-op on web.
 */
export async function decrementAppBadge(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Badge.decrease();
  } catch (error) {
    logger.error('Failed to decrement app icon badge:', error);
  }
}

/**
 * Set the app-icon badge to the user's current unread-notification count
 * (matching what the push server sends as `aps.badge`). Use after partially
 * reading notifications — e.g. tapping one push while others remain unread —
 * so the badge shows the true remaining count instead of lying at 0.
 * No-op on web.
 */
export async function syncAppBadgeToUnread(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);
    if (error || count === null) {
      logger.error('Failed to count unread notifications for badge sync:', error);
      return;
    }

    if (count === 0) {
      await clearAppBadge();
    } else {
      await Badge.set({ count });
    }
  } catch (error) {
    logger.error('Failed to sync app icon badge:', error);
  }
}
